import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { knowledgeBaseAPI } from "../lib/api";
import { formatDateTime } from "../lib/utils";
import { Plus, Search, BookOpen, Edit2, History, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

export function KnowledgeBase() {
  const { user } = useAuth();
  const [kbEntries, setKbEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [historyEntry, setHistoryEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({
    headline: "",
    how_to: ""
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [createImages, setCreateImages] = useState([]); // Temp storage for images during creation
  const [editImages, setEditImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const createFileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  useEffect(() => {
    loadKBEntries();
  }, []);

  const loadKBEntries = async (search = null) => {
    try {
      setLoading(true);
      const response = await knowledgeBaseAPI.getAll(search);
      const sortedEntries = response.data.sort((a, b) => {
        const dateA = new Date(a.created_date || 0);
        const dateB = new Date(b.created_date || 0);
        return dateB - dateA;
      });
      setKbEntries(sortedEntries);
    } catch (error) {
      toast.error("Failed to load knowledge base entries");
      console.error("KB load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, entryId = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem("auth_token");
      const response = await axios.post(
        `${getBaseURL()}/api/knowledge-base/${entryId}/upload-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return response.data.image_url;
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload image");
      return null;
    }
  };

  const downloadImageFromUrl = async (imageUrl, entryId) => {
    try {
      // Fetch the image
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Convert to File
      const file = new File([blob], 'pasted-image.png', { type: blob.type });
      
      // Upload it
      return await handleImageUpload(file, entryId);
    } catch (error) {
      console.error("Failed to download image:", error);
      return null;
    }
  };

  const extractImagesFromHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const images = doc.querySelectorAll('img');
    const imageUrls = [];
    
    images.forEach(img => {
      const src = img.src;
      if (src && (src.startsWith('http') || src.startsWith('data:'))) {
        imageUrls.push(src);
      }
    });
    
    return imageUrls;
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setImageModalOpen(false);
  };

  const handlePasteCreate = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    let foundImage = false;

    // First, check for direct image files (screenshots, etc.)
    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        foundImage = true;
        const file = item.getAsFile();
        if (file) {
          setCreateImages(prev => [...prev, { file, preview: URL.createObjectURL(file) }]);
          toast.success("Image pasted! It will be uploaded when you create the entry.");
        }
      }
    }

    // If no direct image found, check for HTML content with images
    if (!foundImage) {
      for (const item of items) {
        if (item.type === 'text/html') {
          const html = await new Promise((resolve) => {
            item.getAsString(resolve);
          });
          
          const imageUrls = extractImagesFromHtml(html);
          
          if (imageUrls.length > 0) {
            e.preventDefault();
            toast.info(`Found ${imageUrls.length} image(s) in pasted content. Downloading...`);
            
            // Download images through backend proxy (bypasses CORS)
            const token = localStorage.getItem("auth_token");
            let successCount = 0;
            
            for (const url of imageUrls) {
              try {
                // Skip data URLs, handle them directly
                if (url.startsWith('data:')) {
                  const response = await fetch(url);
                  const blob = await response.blob();
                  const file = new File([blob], 'pasted-image.png', { type: blob.type });
                  setCreateImages(prev => [...prev, { file, preview: url }]);
                  successCount++;
                } else {
                  // Use backend proxy for external URLs
                  const response = await axios.post(
                    `${getBaseURL()}/api/knowledge-base/temp/download-external-image`,
                    { image_url: url },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  
                  if (response.data.data_url) {
                    // Convert data URL to file
                    const dataUrl = response.data.data_url;
                    const fetchResponse = await fetch(dataUrl);
                    const blob = await fetchResponse.blob();
                    const file = new File([blob], 'pasted-image.png', { type: blob.type });
                    setCreateImages(prev => [...prev, { file, preview: dataUrl }]);
                    successCount++;
                  }
                }
              } catch (error) {
                console.error("Failed to process image:", url, error);
              }
            }
            
            if (successCount > 0) {
              toast.success(`${successCount} image(s) extracted and ready to upload!`);
            } else {
              toast.error("Failed to download images from website");
            }
          }
        }
      }
    }
  };

  const handleFileSelectCreate = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newImages = Array.from(files).map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setCreateImages(prev => [...prev, ...newImages]);
    toast.success(`${files.length} image(s) added`);
  };

  const removeCreateImage = (index) => {
    setCreateImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };


  const handlePasteEdit = async (e, entryId) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    let foundImage = false;

    // First, check for direct image files (screenshots, etc.)
    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        foundImage = true;
        const file = item.getAsFile();
        if (file && entryId) {
          toast.info("Uploading pasted image...");
          const imageUrl = await handleImageUpload(file, entryId);
          if (imageUrl) {
            setEditImages(prev => [...prev, imageUrl]);
            toast.success("Image pasted and uploaded successfully!");
            loadKBEntries();
          }
        }
      }
    }

    // If no direct image found, check for HTML content with images
    if (!foundImage) {
      for (const item of items) {
        if (item.type === 'text/html') {
          const html = await new Promise((resolve) => {
            item.getAsString(resolve);
          });
          
          const imageUrls = extractImagesFromHtml(html);
          
          if (imageUrls.length > 0) {
            e.preventDefault();
            toast.info(`Found ${imageUrls.length} image(s) in pasted content. Downloading and uploading...`);
            
            let successCount = 0;
            const token = localStorage.getItem("auth_token");
            
            // Download and upload images through backend proxy
            for (const url of imageUrls) {
              try {
                if (url.startsWith('data:')) {
                  // Handle data URLs directly
                  const response = await fetch(url);
                  const blob = await response.blob();
                  const file = new File([blob], 'pasted-image.png', { type: blob.type });
                  const imageUrl = await handleImageUpload(file, entryId);
                  
                  if (imageUrl) {
                    setEditImages(prev => [...prev, imageUrl]);
                    successCount++;
                  }
                } else {
                  // Use backend proxy for external URLs
                  const response = await axios.post(
                    `${getBaseURL()}/api/knowledge-base/${entryId}/download-external-image`,
                    { image_url: url },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  
                  if (response.data.image_url) {
                    setEditImages(prev => [...prev, response.data.image_url]);
                    successCount++;
                  }
                }
              } catch (error) {
                console.error("Failed to process image:", url, error);
              }
            }
            
            if (successCount > 0) {
              toast.success(`${successCount} image(s) uploaded successfully!`);
              loadKBEntries();
            } else {
              toast.error("Failed to download images from website");
            }
          }
        }
      }
    }
  };

  const handleFileSelect = async (e, entryId) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of files) {
      const imageUrl = await handleImageUpload(file, entryId);
      if (imageUrl) {
        if (editingEntry && editingEntry.id === entryId) {
          setEditImages(prev => [...prev, imageUrl]);
        }
      }
    }
    
    toast.success("Image(s) uploaded successfully");
    loadKBEntries();
  };

  const handleDeleteImage = async (entryId, imageUrl) => {
    try {
      const filename = imageUrl.split('/').pop();
      const token = localStorage.getItem("auth_token");
      
      await axios.delete(
        `${getBaseURL()}/api/knowledge-base/${entryId}/images/${filename}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Image deleted");
      loadKBEntries();
      
      if (editingEntry && editingEntry.id === entryId) {
        setEditImages(prev => prev.filter(img => img !== imageUrl));
      }
    } catch (error) {
      console.error("Delete image error:", error);
      toast.error("Failed to delete image");
    }
  };

  const handleCreateEntry = async (e) => {
    e.preventDefault();
    if (!newEntry.headline.trim() || !newEntry.how_to.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await knowledgeBaseAPI.create(newEntry);
      const entryId = response.data.id;
      
      // Upload all temporarily stored images
      if (createImages.length > 0) {
        toast.info(`Uploading ${createImages.length} image(s)...`);
        for (const imageData of createImages) {
          await handleImageUpload(imageData.file, entryId);
          URL.revokeObjectURL(imageData.preview);
        }
      }
      
      toast.success("Knowledge base entry created successfully!");
      setShowCreateDialog(false);
      setNewEntry({ headline: "", how_to: "" });
      setCreateImages([]);
      loadKBEntries();
    } catch (error) {
      toast.error("Failed to create entry");
      console.error("Create error:", error);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEditImages(entry.images || []);
    setShowEditDialog(true);
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    if (!editingEntry.headline.trim() || !editingEntry.how_to.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await knowledgeBaseAPI.update(editingEntry.id, {
        headline: editingEntry.headline,
        how_to: editingEntry.how_to
      });
      toast.success("Entry updated");
      setShowEditDialog(false);
      setEditingEntry(null);
      setEditImages([]);
      loadKBEntries();
    } catch (error) {
      toast.error("Failed to update entry");
      console.error("Update error:", error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await loadKBEntries(searchTerm || null);
  };

  const showHistory = (entry) => {
    setHistoryEntry(entry);
    setShowHistoryDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">OTS Knowledge Base</h1>
          <p className="text-gray-600 mt-2">Searchable knowledge base for staff</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Knowledge Base Entry</DialogTitle>
              <DialogDescription>
                Add a new entry. You can paste or upload images directly.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateEntry} className="space-y-4">
              <div>
                <Label htmlFor="headline">Headline *</Label>
                <Input
                  id="headline"
                  value={newEntry.headline}
                  onChange={(e) => setNewEntry({ ...newEntry, headline: e.target.value })}
                  placeholder="Enter headline"
                  required
                />
              </div>
              <div>
                <Label htmlFor="how_to">How To *</Label>
                <Textarea
                  id="how_to"
                  value={newEntry.how_to}
                  onChange={(e) => setNewEntry({ ...newEntry, how_to: e.target.value })}
                  onPaste={handlePasteCreate}
                  placeholder="Enter instructions (you can paste images here)"
                  rows={6}
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  💡 Tip: You can paste images directly (Ctrl+V or Cmd+V) - supports screenshots, image files, and content copied from websites with images
                </p>
              </div>
              
              <div>
                <Label>Images</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    ref={createFileInputRef}
                    onChange={handleFileSelectCreate}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => createFileInputRef.current?.click()}
                  >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Upload Images
                  </Button>
                </div>
                
                {createImages.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                    {createImages.map((imageData, idx) => (
                      <div key={imageData.preview || idx} className="relative group">
                        <img
                          src={imageData.preview}
                          alt={`Preview ${idx + 1}`}
                          className="w-full h-32 object-cover rounded-md border"
                        />
                        <button
                          type="button"
                          onClick={() => removeCreateImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <Button type="submit" className="flex-1">Create Entry</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateDialog(false);
                    createImages.forEach(img => URL.revokeObjectURL(img.preview));
                    setCreateImages([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <form onSubmit={handleSearch} className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search knowledge base..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button type="submit">Search</Button>
        {searchTerm && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setSearchTerm("");
              loadKBEntries();
            }}
          >
            Clear
          </Button>
        )}
      </form>

      <div className="space-y-4">
        {kbEntries.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No knowledge base entries found</p>
            </CardContent>
          </Card>
        ) : (
          kbEntries.map((entry) => (
            <Card key={entry.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{entry.headline}</CardTitle>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditEntry(entry)}
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {entry.edit_history && entry.edit_history.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => showHistory(entry)}
                      >
                        <History className="h-4 w-4 mr-1" />
                        History ({entry.edit_history.length})
                      </Button>
                    )}
                  </div>
                </div>
                <CardDescription>
                  Created by {entry.created_by} on {formatDateTime(entry.created_date)}
                  {entry.last_edited_by && (
                    <span className="ml-2 text-blue-600">
                      • Last edited by {entry.last_edited_by}
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="whitespace-pre-wrap">{entry.how_to}</p>
                </div>
                
                {entry.images && entry.images.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {entry.images.map((imageUrl, idx) => (
                      <div 
                        key={imageUrl || idx} 
                        className="relative group cursor-pointer"
                        onClick={() => openImageModal(imageUrl)}
                      >
                        <img
                          src={`${getBaseURL()}/api${imageUrl}`}
                          alt={`KB Image ${idx + 1}`}
                          className="w-full h-32 object-cover rounded-md border hover:opacity-80 transition-opacity"
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30 rounded-md">
                          <span className="text-white text-sm font-medium">Click to enlarge</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Knowledge Base Entry</DialogTitle>
            <DialogDescription>
              Update the entry below. Images can be pasted (Ctrl+V) or uploaded.
            </DialogDescription>
          </DialogHeader>
          {editingEntry && (
            <form onSubmit={handleUpdateEntry} className="space-y-4">
              <div>
                <Label htmlFor="edit-headline">Headline *</Label>
                <Input
                  id="edit-headline"
                  value={editingEntry.headline}
                  onChange={(e) => setEditingEntry({ ...editingEntry, headline: e.target.value })}
                  placeholder="Enter headline"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-how-to">How To *</Label>
                <Textarea
                  id="edit-how-to"
                  value={editingEntry.how_to}
                  onChange={(e) => setEditingEntry({ ...editingEntry, how_to: e.target.value })}
                  onPaste={(e) => handlePasteEdit(e, editingEntry.id)}
                  placeholder="Enter instructions (you can paste images here)"
                  rows={6}
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  💡 Tip: You can paste images directly (Ctrl+V or Cmd+V) - supports screenshots, image files, and content copied from websites with images
                </p>
              </div>
              
              <div>
                <Label>Images</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    ref={editFileInputRef}
                    onChange={(e) => handleFileSelect(e, editingEntry.id)}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => editFileInputRef.current?.click()}
                  >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Upload Images
                  </Button>
                </div>
                
                {editImages.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                    {editImages.map((imageUrl, idx) => (
                      <div key={imageUrl || idx} className="relative group">
                        <img
                          src={`${getBaseURL()}/api${imageUrl}`}
                          alt={`Image ${idx + 1}`}
                          className="w-full h-32 object-cover rounded-md border"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(editingEntry.id, imageUrl)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <Button type="submit" className="flex-1">Update Entry</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingEntry(null);
                    setEditImages([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit History</DialogTitle>
            <DialogDescription>
              View all changes made to this entry
            </DialogDescription>
          </DialogHeader>
          {historyEntry && historyEntry.edit_history && (
            <div className="space-y-4">
              {historyEntry.edit_history.map((edit, idx) => (
                <Card key={`${edit.edited_by}-${edit.edited_date || idx}`}>
                  <CardHeader>
                    <CardTitle className="text-sm">
                      Edited by {edit.edited_by} on {formatDateTime(edit.edited_date)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    {edit.old_headline && (
                      <div>
                        <p className="font-medium">Headline changed:</p>
                        <p className="text-gray-600">From: {edit.old_headline}</p>
                        <p className="text-blue-600">To: {edit.new_headline}</p>
                      </div>
                    )}
                    {edit.old_how_to && (
                      <div>
                        <p className="font-medium">How To changed:</p>
                        <p className="text-gray-600 whitespace-pre-wrap">From: {edit.old_how_to}</p>
                        <p className="text-blue-600 whitespace-pre-wrap">To: {edit.new_how_to}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Lightbox Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] p-2">
          <DialogHeader>
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 overflow-auto">
            {selectedImage && (
              <img
                src={`${getBaseURL()}/api${selectedImage}`}
                alt="Full size preview"
                className="max-w-full max-h-[80vh] object-contain rounded-md"
                onClick={closeImageModal}
              />
            )}
          </div>
          <div className="text-center text-sm text-gray-500 pb-2">
            Click image or outside to close
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
